import React, { useState, useEffect, Fragment } from "react";

import useGet from "../../hooks/useGet";
import Loading from "../Loading";
import DiseaseList from "./DiseaseList";
import Error from "./Error";
import AlphabetFilters from "./filters/Alphabet";
import CustomSelect from "../ui/CustomSelect";

const apiUrl = "/wp-json/salesforce/v1/funds";

// -----------------------
// Insurance helpers
// -----------------------
function splitSemicolonList(str) {
  if (!str) return [];
  return String(str)
    .split(";")
    .map(function (s) { return s.trim(); })
    .filter(Boolean);
}

function isMedicareOrMAOnly(fund) {
  const allowed = [
    "Medicare (Original Fee-for-Service, Parts A, B, or D)",
    "Medicare Advantage",
    "Medicaid (Medicaid, Medicaid HMO, MediCal, or CHIP)",
    "TRICARE"
  ];

  const pub = splitSemicolonList(fund && fund.Public_Allowed_Insurance_Types);

  // No public insurance types at all → not a match
  if (!pub || !pub.length) return false;

  // Return true if at least one of the allowed values is present
  return pub.some(function (x) {
    return allowed.indexOf(x) > -1;
  });
}

function isAnyInsurance(fund) {
  var pub = (fund && fund.Public_Allowed_Insurance_Types) ? String(fund.Public_Allowed_Insurance_Types).trim() : "";
  var com = (fund && fund.Commercial_Allowed_Insurance_Types) ? String(fund.Commercial_Allowed_Insurance_Types).trim() : "";
  return pub === "" && com === "";
}

// -----------------------
// Fund Type helper
// -----------------------
function normalizeFundType(raw) {
  var s = String(raw || "").trim().toLowerCase();
  if (s === "co-pay" || s === "co pay") return "med_copay";
  if (s === "premium assistance") return "premium";
  if (s === "card-supported fund" || s === "card supported fund") return "transportation";
  return ""; // unknown / not filterable
}

// -----------------------
// Permalink ↔ title helpers
// -----------------------
function normalizePermalink(url) {
  var s = String(url || "");
  try {
    var a = document.createElement("a");
    a.href = s;
    var clean = a.protocol ? (a.protocol + "//" + a.host + a.pathname) : s;
    clean = clean.replace(/\/+$/, "");
    return clean;
  } catch (e) {
    return s.replace(/\/+$/, "");
  }
}

function lookupTitleFromMap(permalink, map) {
  if (!permalink || !map) return "";
  var exact = map[permalink];
  if (exact) return exact;

  var norm = normalizePermalink(permalink);
  if (map[norm]) return map[norm];
  if (map[norm + "/"]) return map[norm + "/"];

  if (permalink.charAt(permalink.length - 1) !== "/") {
    var withSlash = permalink + "/";
    if (map[withSlash]) return map[withSlash];
  }
  return "";
}

const Diseases = ({ titlesMap = {} }) => {
  // Alphabet flags
  var activeLetters = [];
  var hasReEnrollment = false;
  var hasWaitListOnly = false;

  const [letter, setLetter] = useState("");

  // Booleans expected by <DiseaseList />
  const [open, setOpen] = useState(true);
  const [closed, setClosed] = useState(true);
  const [reEnrollment, setReEnrollment] = useState(true);
  const [waitListOnly, setWaitListOnly] = useState(true);

  // "all" | "open" | "closed"
  const [status, setStatus] = useState("all");

  // Insurance single-select: "all" | "medicare" | "any"
  const [insurance, setInsurance] = useState("all");

  // Fund type single-select: "all" | "med_copay" | "premium" | "transportation"
  const [fundType, setFundType] = useState("all");

  const [loading, response, error] = useGet(apiUrl);

  // Build activeLetters and flags
  if (response && !loading && typeof response.data === "object" && response.data.funds) {
    var lettersMap = {};
    for (var i = 0; i < response.data.funds.length; i++) {
      var fund = response.data.funds[i];

      if (fund && fund.Fund_Status === "Open" && fund.IsRenewalOnly) {
        hasReEnrollment = true;
      }
      if (fund && fund.Fund_Status === "Closed, wait list available") {
        hasWaitListOnly = true;
      }

      var nameSource = "";
      if (fund && fund.Fund_Name) {
        nameSource = fund.Fund_Name;
      } else if (fund && fund.Disease) {
        nameSource = fund.Disease;
      }
      var first = (nameSource ? String(nameSource) : "")
        .trim()
        .charAt(0)
        .toUpperCase();
      if (first >= "A" && first <= "Z") {
        lettersMap[first] = true;
      }
    }
    activeLetters = Object.keys(lettersMap).sort();
  }

  // Map status -> booleans for <DiseaseList />
  useEffect(function () {
    switch (status) {
      case "open":
        setOpen(true);
        setClosed(false);
        setWaitListOnly(false);
        setReEnrollment(true);
        break;
      case "closed":
        setOpen(false);
        setClosed(true);
        setWaitListOnly(false);
        setReEnrollment(true);
        break;
      case "all":
      default:
        setOpen(true);
        setClosed(true);
        setWaitListOnly(true);
        setReEnrollment(true);
        break;
    }
  }, [status]);

  // --- Status select with dynamic first option label ---
  var statusEmptyLabel = (status === "all") ? "Status" : "(show all)";
  var statusOptions = [
    { value: "all", label: statusEmptyLabel },
    {
      value: "open",
      label: (
        <span>
          <strong>Open</strong>{" "}
          <span className="c-select__desc">
            (accepting applications for new and renewal grants, and additional funds)
          </span>
        </span>
      )
    },
    {
      value: "closed",
      label: (
        <span>
          <strong>Closed</strong>{" "}
          <span className="c-select__desc">
            (not currently accepting applications: sign up for wait list)
          </span>
        </span>
      )
    }
  ];

  // --- Insurance select with dynamic first option label ---
  var insuranceEmptyLabel = (insurance === "all") ? "Type of insurance" : "(show all)";
  var insuranceOptions = [
    { value: "all", label: insuranceEmptyLabel },
    { value: "medicare", label: "Government-insured (Medicare, MA, Medicaid, and TRICARE plans)" },
    { value: "any", label: "Any insurance type" },
  ];

  // Fund Type select with dynamic first option label
  var fundTypeEmptyLabel = (fundType === "all") ? "Type of fund" : "(show all)";
  var fundTypeOptions = [
    { value: "all", label: fundTypeEmptyLabel },
    { value: "med_copay", label: "Medication copay" },
    { value: "premium", label: "Health insurance premium" },
    { value: "transportation", label: "Transportation" }
  ];

  // Apply insurance + type filters client-side
  var allFunds = (response && !loading && response.data) ? response.data.funds : null;

  var filteredByInsurance = allFunds;
  if (Array.isArray(allFunds) && insurance !== "all") {
    filteredByInsurance = allFunds.filter(function (fund) {
      if (insurance === "medicare") {
        return isMedicareOrMAOnly(fund);
      }
      if (insurance === "any") {
        return isAnyInsurance(fund);
      }
      return true;
    });
  }

  var filteredByType = filteredByInsurance;
  if (Array.isArray(filteredByInsurance) && fundType !== "all") {
    filteredByType = filteredByInsurance.filter(function (fund) {
      return normalizeFundType(fund && fund.Type) === fundType;
    });
  }

  // Enrich each fund with wpTitle from titlesMap (by permalink)
  var fundsForList = Array.isArray(filteredByType)
    ? filteredByType.map(function (f) {
        var out = Object.assign({}, f);
        var pl = f && f.permalink;
        out.wpTitle = lookupTitleFromMap(pl, titlesMap);
        return out;
      })
    : filteredByType;

  return (
    <Fragment>
      <form className="ListingFilters u-form">
        <div className="l-padding">
          <div className="ListingFilters-wrap l-wrap--disease-fund-listing">
            <span className="filter-by-label">
              <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <path d="M10 18.2451V16.2451H14V18.2451H10ZM6 13.2451V11.2451H18V13.2451H6ZM3 8.24512V6.24512H21V8.24512H3Z" fill="#2B0736"/>
              </svg>
              Filter By
            </span>

            <AlphabetFilters
              activeLetters={activeLetters}
              setLetters={setLetter}
              letter={letter}
            />

            {/* Status filter */}
            <fieldset>
              <CustomSelect
                id="disease-fund-status"
                value={status}
                onChange={function (val) { setStatus(val); }}
                placeholder="Status"
                options={statusOptions}
                className="c-select"
                triggerClassName="c-select__trigger"
                menuClassName="c-select__menu"
                optionClassName="c-select__option"
                ariaLabel="Filter funds by status"
              />
            </fieldset>

            {/* Insurance filter */}
            <fieldset>
              <CustomSelect
                id="disease-fund-insurance"
                value={insurance}
                onChange={function (val) { setInsurance(val); }}
                placeholder="Type of insurance"
                options={insuranceOptions}
                className="c-select"
                triggerClassName="c-select__trigger"
                menuClassName="c-select__menu"
                optionClassName="c-select__option"
                ariaLabel="Filter funds by insurance type"
              />
            </fieldset>

            {/* NEW: Type of fund filter */}
            <fieldset>
              <CustomSelect
                id="disease-fund-type"
                value={fundType}
                onChange={function (val) { setFundType(val); }}
                placeholder="Type of fund"
                options={fundTypeOptions}
                className="c-select"
                triggerClassName="c-select__trigger"
                menuClassName="c-select__menu"
                optionClassName="c-select__option"
                ariaLabel="Filter funds by type of fund"
              />
            </fieldset>

            <button
              type="button"
              className="c-linkButton"
              onClick={function () {
                setLetter("");
                setStatus("all");
                setInsurance("all");
                setFundType("all"); // reset new filter
              }}
            >
              Show All
            </button>
          </div>
        </div>
      </form>

      <div className="l-padding">
        <div className="l-wrap--disease-fund-listing">
          {loading && <Loading label="Searching..." />}

          {response && !loading && (
            <DiseaseList
              letter={letter}
              open={open}
              closed={closed}
              reEnrollment={reEnrollment}
              waitListOnly={waitListOnly}
              displayStatus={true}
              results={fundsForList}
            />
          )}

          {error && <Error />}
        </div>
      </div>
    </Fragment>
  );
};

export default Diseases;
